'use client';

import { useEffect, useMemo, useState } from 'react';
import { useProductDetails } from '@app/pdp/hooks';
import { ShowMore } from '@petsmart-ui/sparky';
import { getProductPromotions } from '@libs/ocapi/server';
import type { EligiblePromotion } from '@libs/common-types';
import type { ProductPromotion } from '@libs/common-next';
import {
    filterActiveAlgoliaPromotions,
    sortProductAlgoliaPromotions,
} from '@libs/common-next';
import { type ProductPromotionsItemsProps } from './types';
import PromoList from './PromoList';
import './ProductPromotions.scss';

export default function ProductPromotionsItems({
    defaultProductPromotions,
    maxDisplayLength = 2,
    product,
    isCTEnabled,
}: ProductPromotionsItemsProps) {
    const productPromotionsMap = useMemo(
        () => new Map<string, ProductPromotion[] | EligiblePromotion[]>(),
        []
    );

    const sortProductPromotions = (productPromotions: ProductPromotion[]) =>
        [...productPromotions].sort((a, b) => (b?.rank ?? 0) - (a?.rank ?? 0));

    const { selectedProduct } = useProductDetails();

    const [productPromotions, setProductPromotions] = useState(
        isCTEnabled
            ? sortProductAlgoliaPromotions(
                  defaultProductPromotions as EligiblePromotion[]
              )
            : sortProductPromotions(
                  defaultProductPromotions as ProductPromotion[]
              )
    );

    // Fetch all promotions for all product variants after page is loaded
    useEffect(() => {
        if (!product) return;

        const productVariants = product.productVariants?.variants || [];

        const fetchProductPromotions = async () => {
            const productPromotionsPromises = productVariants.map(variant =>
                getProductPromotions(variant.id.toString(), true)
            );

            const allProductPromotions = await Promise.all(
                productPromotionsPromises
            );

            allProductPromotions.forEach((promotions, index) => {
                const variant = productVariants[index];

                productPromotionsMap.set(
                    variant.id.toString(),
                    sortProductPromotions(promotions)
                );
            });
        };

        const setCTProductPromotions = () => {
            productVariants.forEach(variant => {
                if (!variant?.eligiblePromotions) return;

                const activePromos = filterActiveAlgoliaPromotions({
                    promotions: variant.eligiblePromotions,
                });

                productPromotionsMap.set(
                    variant.id.toString(),
                    sortProductAlgoliaPromotions(activePromos)
                );
            });
        };

        if (isCTEnabled) {
            setCTProductPromotions();
        } else {
            fetchProductPromotions();
        }
    }, [product, productPromotionsMap, isCTEnabled]);

    // Get promotions for selected product
    useEffect(() => {
        if (selectedProduct) {
            const promotions = productPromotionsMap.get(
                selectedProduct.id.toString()
            );

            if (promotions) {
                setProductPromotions(promotions);
            }
        }
    }, [selectedProduct, productPromotionsMap]);

    const { length: promoLength } = productPromotions;
    if (promoLength === 0) {
        return null;
    }

    return promoLength > maxDisplayLength ? (
        <ShowMore
            data-testid="product-promotions"
            className="product-promotions"
            buttonText={`Show more offers (${promoLength - maxDisplayLength})`}
            hideButtonText="Show less"
            iconName="chevron-down"
            iconPosition="after"
            hiddenContent={
                <PromoList
                    promotions={productPromotions.slice(maxDisplayLength)}
                    isCTEnabled={isCTEnabled}
                />
            }
        >
            <PromoList
                promotions={productPromotions.slice(0, maxDisplayLength)}
                isCTEnabled={isCTEnabled}
            />
        </ShowMore>
    ) : (
        <div className="product-promotions" data-testid="product-promotions">
            <PromoList
                promotions={productPromotions}
                isCTEnabled={isCTEnabled}
            />
        </div>
    );
}
